import { useRef, useEffect } from 'react';

/**
 * Interactive dot-field background (canvas 2D). Dots sit on a fixed grid; near
 * the cursor they are pushed outward (cursorForce, within cursorRadius) and
 * glow — brighten + enlarge — within glowRadius, easing back to rest otherwise.
 * Params mirror reactbits.dev/backgrounds/dot-field.
 */
export default function DotField({
  dotRadius = 2,
  dotSpacing = 10,
  cursorForce = 0.49,
  cursorRadius = 350,
  glowRadius = 230,
  dotColor = '255, 184, 249', // #FFB8F9 as "r, g, b"
  baseAlpha = 0.13,
  className,
  style,
}) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let dots = [];
    let raf = 0;
    const mouse = { x: -1e5, y: -1e5, active: false };

    function build() {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      dots = [];
      const ox = (w % dotSpacing) / 2;
      const oy = (h % dotSpacing) / 2;
      for (let y = oy; y <= h; y += dotSpacing) {
        for (let x = ox; x <= w; x += dotSpacing) {
          dots.push({ bx: x, by: y, x, y });
        }
      }
    }

    function frame() {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      ctx.clearRect(0, 0, w, h);
      const pushMax = cursorRadius * 0.07; // max displacement, px (halved — subtler expansion)
      for (let i = 0; i < dots.length; i++) {
        const d = dots[i];
        const dx = d.bx - mouse.x;
        const dy = d.by - mouse.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        let tx = d.bx;
        let ty = d.by;
        if (mouse.active && dist < cursorRadius && dist > 0.001) {
          const f = Math.pow(1 - dist / cursorRadius, 2) * cursorForce;
          tx = d.bx + (dx / dist) * f * pushMax;
          ty = d.by + (dy / dist) * f * pushMax;
        }
        d.x += (tx - d.x) * 0.12;
        d.y += (ty - d.y) * 0.12;

        let a = baseAlpha;
        let r = dotRadius;
        if (mouse.active && dist < glowRadius) {
          const g = 1 - dist / glowRadius;
          a = baseAlpha + g * (1 - baseAlpha);
          r = dotRadius * (1 + g * 0.15); // bloom reduced again (0.3 -> 0.15)
        }
        ctx.beginPath();
        ctx.arc(d.x, d.y, r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${dotColor}, ${a})`;
        ctx.fill();
      }
      raf = requestAnimationFrame(frame);
    }

    function onMove(e) {
      const rect = canvas.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
      mouse.active = true;
    }
    function onLeave() { mouse.active = false; }
    let rt;
    function onResize() { clearTimeout(rt); rt = setTimeout(build, 120); }

    build();
    frame();
    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerleave', onLeave);
    window.addEventListener('blur', onLeave);
    window.addEventListener('resize', onResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerleave', onLeave);
      window.removeEventListener('blur', onLeave);
      window.removeEventListener('resize', onResize);
    };
  }, [dotRadius, dotSpacing, cursorForce, cursorRadius, glowRadius, dotColor, baseAlpha]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{ display: 'block', width: '100%', height: '100%', ...style }}
    />
  );
}
